// src/app/Sentry/sentry-filters.ts
//
// Pure, side-effect-free predicates used by Sentry's beforeSend filters in sentry.config.ts.
// Kept separate so they can be unit-tested without importing the Sentry SDK, the environment
// config, or triggering Sentry initialization.
//
// Module-load safety: nothing in this file touches window, navigator or location at import
// time. Browser globals are only read lazily inside function bodies, behind typeof guards.

// Angular lazy-loaded route chunks for the sales/registration flow whose Sentry frame filename
// is the route path (e.g. /free-reg-step4c or free-reg-step4c) rather than a hashed bundle name.
// Used by isVettedSalesRouteChunk for stack-frame filename matching only, and only ever tested
// against the PATH portion of a filename (query string and hash are stripped first).
// Leading-slash is optional via (^|\/) so both bare and rooted path forms are matched.
// The terminator (?:\/|$) covers path separators and end-of-path.
// No g/y flag: the regex is stateless and safe to reuse across multiple .test() calls.
export const SALES_ROUTE_CHUNK_RE = /(^|\/)(free-reg-step4[abcdgh]|free-reg-complete-profile|partial-registration|quick-launch)(?:\/|$)/;

// Stricter form used for RELATIVE/bare filenames, which carry no origin: the route must be the
// first path segment (optionally rooted), e.g. "free-reg-step4a", "/free-reg-step4a/x". Host-like
// or nested relative strings such as "evil.com/free-reg-step4a" or "third-party/free-reg-step4a"
// are rejected so they can never be treated as ours.
const RELATIVE_SALES_ROUTE_RE = /^\/?(free-reg-step4[abcdgh]|free-reg-complete-profile|partial-registration|quick-launch)(?:\/|$)/;

// Production app host. Single source of truth for the Sentry filters; Sentry is only
// initialized in production builds (see initSentry), so this is not environment-specific.
export const APP_HOST = 'app.lottosocial.com';
const APP_ORIGIN = `https://${APP_HOST}`;

/**
 * True when filename's PATH is a vetted sales-route chunk path. The filename is either
 * relative/bare (WebKit attributes inline/injected scripts to the page path, with no origin) or
 * an absolute URL whose origin is exactly https://app.lottosocial.com (scheme, host and port).
 *
 * Only the path is matched: a route that appears solely in a query string or hash
 * (e.g. /some-page?next=/free-reg-step4a) is NOT vetted. http:, other ports and other hosts are
 * rejected so spoofed or third-party frame filenames are never treated as ours.
 *
 * Relative filenames are path-vetted only (they cannot prove their origin). That is intentional:
 * it is how WebKit reports Meta's injected script. On its own a relative path never drops an
 * event; callers combine it with much more specific evidence (see shouldDropFacebookIOSBridgeError).
 */
export function isVettedSalesRouteChunk(filename: string): boolean {
    if (!filename) return false;

    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(filename);
    const isSchemeRelative = filename.startsWith('//');

    if (!hasScheme && !isSchemeRelative) {
        // Relative/bare path: strip query and hash without URL parsing, so an opaque ("null")
        // page origin cannot cause a valid relative filename to be rejected. Only path-vetted
        // (there is no origin to check), so require the route as the first path segment.
        const path = filename.split(/[?#]/, 1)[0];
        return RELATIVE_SALES_ROUTE_RE.test(path);
    }

    try {
        // Scheme-relative URLs are resolved against the production origin. Substring checks
        // like includes(APP_HOST) are bypassable, so compare the full origin exactly.
        const url = new URL(filename, APP_ORIGIN);
        return url.origin === APP_ORIGIN && SALES_ROUTE_CHUNK_RE.test(url.pathname);
    } catch {
        return false;
    }
}

// Call chain of the login-form tracking script injected by Facebook's iOS in-app browser
// (LOTTOSOCIAL-PWA-1NCB), ordered outermost caller -> innermost callee, matching Sentry's
// frame order (oldest first). None of these functions exist in our codebase. The chain must
// appear as ADJACENT frames (no intervening frames), exactly as in the captured event, so an
// unrelated stack that merely contains these fairly generic names is never matched.
//
// The trailing native `postMessage` frame ([native code]) is deliberately NOT required: native
// frames are dropped or renamed inconsistently across WebKit versions and Sentry stack parsing,
// so requiring it would make the filter silently stop matching. Specificity comes from the
// adjacent three-function chain + vetted path + Meta iOS UA + InvalidAccessError, all together.
const FB_IOS_IAB_BRIDGE_CALL_CHAIN: readonly string[] = [
    'logLoginFormFocused',
    'sendPostMessage',
    'dispatchToBridge',
];

/**
 * True inside Meta's iOS in-app browsers (Facebook and Messenger), which share the same
 * WKWebView shell and injected script, and carry FBAN/FBAV tokens. The name keeps "Facebook"
 * because the issue was reported from the Facebook app; Messenger is matched intentionally.
 * Requires both a Meta app token and an iOS device token so Android Facebook WebViews (which
 * also carry FBAN/FBAV/FB_IAB) are never matched. Accepts an explicit UA for testability;
 * defaults to navigator.userAgent and safely returns false when navigator is unavailable.
 */
export function isFacebookIOSInAppBrowser(
    ua: string = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
): boolean {
    // Word boundaries avoid accidental substring matches (e.g. "XFBANY" or "iPhoneless").
    const isMetaApp = /\b(?:FBAN|FBAV|FB_IAB|FBIOS)\b/i.test(ua);
    const isIOS = /\b(?:iPhone|iPad|iPod|FBIOS)\b/i.test(ua);
    return isMetaApp && isIOS;
}

/**
 * @internal Exported for unit tests only; not part of the module's public API.
 *
 * Normalizes a Sentry frame function name so wrapped/prefixed variants still match, e.g.
 * "Object.dispatchToBridge", "async sendPostMessage", "async* x", "bound x",
 * "logLoginFormFocused/<" -> bare name.
 */
export function normalizeFrameFunction(name?: string | null): string {
    if (!name) return '';
    // Strip leading wrapper keywords Sentry/browsers emit: "async ", "async*", "bound ".
    // "async" must be followed by "*" or whitespace so names like "asyncFoo" are left intact.
    const withoutWrappers = name.replace(/^(?:async(?:\*\s*|\s+)|bound\s+)+/, '');
    const lastSegment = withoutWrappers.split('.').pop() ?? '';
    return lastSegment.replace(/[^A-Za-z0-9_$].*$/, '');
}

// Nullable to match Sentry SDK event shapes, which may normalize missing fields to null.
type FrameLike = { function?: string | null; filename?: string | null };

type ExceptionLike = {
    type?: string | null;
    value?: string | null;
    stacktrace?: { frames?: FrameLike[] | null } | null;
};

function frameMatches(frame: FrameLike, expected: string): boolean {
    return (
        normalizeFrameFunction(frame.function) === expected &&
        !!frame.filename &&
        isVettedSalesRouteChunk(frame.filename)
    );
}

/** True when `chain` appears as a contiguous run of frames anywhere in `frames`. */
function containsAdjacentChain(frames: ReadonlyArray<FrameLike>, chain: readonly string[]): boolean {
    for (let start = 0; start + chain.length <= frames.length; start++) {
        if (chain.every((expected, i) => frameMatches(frames[start + i], expected))) {
            return true;
        }
    }
    return false;
}

/**
 * True when the frames contain the full Meta bridge call chain as adjacent frames, in order,
 * with each frame's filename a vetted sales-route path on our own origin (where WebKit
 * attributes the injected script).
 */
export function hasFacebookIOSBridgeCallChain(frames: ReadonlyArray<FrameLike>): boolean {
    // Sentry normalizes frames oldest -> newest (as in the captured LOTTOSOCIAL-PWA-1NCB event:
    // ..., logLoginFormFocused, sendPostMessage, dispatchToBridge, postMessage). Also accept the
    // reverse order so the filter doesn't silently stop matching if frames ever arrive newest-first.
    return (
        containsAdjacentChain(frames, FB_IOS_IAB_BRIDGE_CALL_CHAIN) ||
        containsAdjacentChain(frames, [...FB_IOS_IAB_BRIDGE_CALL_CHAIN].reverse())
    );
}

// WebKit's message for DOMException code 15 (InvalidAccessError), as seen in LOTTOSOCIAL-PWA-1NCB.
const INVALID_ACCESS_WEBKIT_MESSAGE = 'does not support the operation or argument';

/**
 * True when an exception is an InvalidAccessError. Sentry may report a DOMException either with
 * type "InvalidAccessError", or with type "DOMException" and the name (or WebKit's message) in
 * the value. All comparisons are case-insensitive.
 *
 * This predicate is intentionally broad and is NEVER sufficient to drop an event on its own;
 * shouldDropFacebookIOSBridgeError also requires the Meta iOS UA and the adjacent bridge chain.
 */
export function isInvalidAccessException(type?: string | null, value?: string | null): boolean {
    const lowerType = (type ?? '').toLowerCase();
    if (lowerType === 'invalidaccesserror') return true;
    if (lowerType !== 'domexception' || !value) return false;
    const lowerValue = value.toLowerCase();
    return lowerValue.includes('invalidaccesserror') || lowerValue.includes(INVALID_ACCESS_WEBKIT_MESSAGE);
}

// [LOTTOSOCIAL-PWA-1NCT] Matches the VALUE field of a Sentry exception entry for a minified
// Angular/Firebase internal error. In Sentry's event format, exception.values[n].type holds
// the error class ("Error") and exception.values[n].value holds only the message — e.g. "La",
// NOT "Error: La". The UI renders it as "Error: La" by combining the two fields.
// Letters-only [A-Za-z]{1,3} excludes digit-containing codes like "404" or "E2" while still
// covering minified identifiers. All-letter acronyms (e.g. "SDK") are also matched; the
// required onunhandledrejection mechanism check is the primary guard against false positives.
const MINIFIED_ERROR_VALUE_RE = /^[A-Za-z]{1,3}$/;

/** True when a single Sentry exception entry looks like a minified internal error. */
function isMinifiedErrorPrimary(ex: ExceptionLike): boolean {
    return ex.type === 'Error' && MINIFIED_ERROR_VALUE_RE.test(ex.value ?? '');
}

/**
 * [LOTTOSOCIAL-PWA-1NCT] Full beforeSend drop decision: returns true only when the PRIMARY
 * exception (a) arrived via onunhandledrejection AND (b) has type "Error" with a 1–3 letter
 * message value (e.g. "La"). Both conditions are checked on the SAME exception entry so a
 * chained event where one exception has the mechanism and a different one has the short
 * value is never dropped.
 *
 * Sentry event format: exception.values[n].type = "Error", .value = "La" (message only).
 * The UI prefixes the type to render "Error: La" — but the value field is just "La".
 *
 * Primary exception ordering: Sentry places exception.values oldest-cause-first, so the
 * top-level (primary) exception is the LAST entry — matching the convention used by
 * shouldDropFacebookIOSBridgeError in this file.
 *
 * exceptions: the full Sentry event.exception.values array (null/undefined → false).
 */
export function isUnhandledRejectionWrappedMinifiedError(
    exceptions: ReadonlyArray<ExceptionLike & { mechanism?: { type?: string | null } | null }> | null | undefined
): boolean {
    const list = exceptions ?? [];
    if (list.length === 0) return false;
    // Primary exception is the last entry (oldest-cause-first ordering).
    const primary = list[list.length - 1];
    return (
        !!primary &&
        primary.mechanism?.type === 'onunhandledrejection' &&
        isMinifiedErrorPrimary(primary)
    );
}

/**
 * Full beforeSend decision for LOTTOSOCIAL-PWA-1NCB. Drops the event only when ALL hold:
 *   1. the UA is Meta's iOS in-app browser,
 *   2. the PRIMARY (top-level, last) exception is an InvalidAccessError, and
 *   3. that SAME exception's stack contains the adjacent Meta bridge call chain on a vetted path.
 * Accepts undefined/empty exceptions and null fields (returns false) and never throws.
 */
export function shouldDropFacebookIOSBridgeError(
    exceptions: ReadonlyArray<ExceptionLike> | null | undefined,
    ua?: string
): boolean {
    if (!isFacebookIOSInAppBrowser(ua)) return false;
    // Only the PRIMARY exception is considered. Sentry orders exception.values oldest cause
    // first, so the top-level (primary) exception is the LAST entry. A real application error
    // whose cause chain merely contains this third-party error must never be dropped.
    const list = exceptions ?? [];
    const primary = list.length > 0 ? list[list.length - 1] : undefined;
    return (
        !!primary &&
        isInvalidAccessException(primary.type, primary.value) &&
        hasFacebookIOSBridgeCallChain(primary.stacktrace?.frames ?? [])
    );
}
